import { useQuery } from "@tanstack/react-query";
import { httpClient } from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { playerAppOrigin } from "@/libs/playerAppOrigin";

const RESOLVE: Record<string, string> = {
  facility_settings: "/settings/facility",
  organization_settings: "/getting-started",
  courts: "/courts",
  operating_hours: "/settings/facility",
  rates: "/settings/rates",
  coaches: "/staff",
  programs: "/programs",
  memberships: "/memberships",
  payments: "/getting-started",
  drop_in: "/services/drop-in",
};

export function playerFacilityUrl(
  facilityId: string,
  host = window.location.hostname,
): string {
  const origin = playerAppOrigin(host);
  return `${origin}/facility/${encodeURIComponent(facilityId)}`;
}

const LABELS: Record<string, string> = {
  court_reservation: "Court booking",
  coaching: "Coaches",
  classes: "Classes",
  open_play: "Drop-in",
  memberships: "Memberships",
};

const RESOLUTION_LABELS: Record<string, string> = {
  facility_settings: "Open facility settings",
  organization_settings: "Open business setup",
  courts: "Set up courts",
  operating_hours: "Set business hours",
  rates: "Open rates",
  coaches: "Manage coaches",
  programs: "Manage classes",
  memberships: "Manage memberships",
  payments: "Open payment setup",
  drop_in: "Set up Drop-in",
};

function resolutionLabel(target: string): string {
  return RESOLUTION_LABELS[target] ?? "Open setup";
}

function listingResolutionStatus(codes: ReadonlySet<string>): string {
  if (codes.has("parent_org_not_approved"))
    return "Ready after business approval";
  if (codes.has("identity_incomplete")) return "Ready after identity setup";
  if (codes.has("facility_inactive")) return "Ready after activation";
  if (codes.has("facility_not_approved")) return "Ready after facility review";
  return "Ready after listing setup";
}

export function FacilityVisibility({ facilityId }: { facilityId: string }) {
  const query = useQuery({
    queryKey: ["facility-discovery-readiness", facilityId],
    queryFn: () =>
      httpClient.get<Schema["FacilityDiscoveryReadinessResponse"]>(
        `/facilities/${encodeURIComponent(facilityId)}/discovery-readiness`,
      ),
  });
  if (query.isPending)
    return <Typography role="status">Checking player visibility…</Typography>;
  if (query.isError)
    return (
      <Alert
        severity="warning"
        action={<Button onClick={() => void query.refetch()}>Retry</Button>}
      >
        Could not check player visibility.
      </Alert>
    );
  const listingCodes = new Set(
    (query.data.listing_blockers ?? []).map((reason) => reason.code),
  );
  const enabledServices = (query.data.services ?? []).filter(
    (item) => item.enabled,
  );
  const disabledServices = (query.data.services ?? [])
    .filter((item) => !item.enabled)
    .map((item) => LABELS[item.service] ?? "Service");
  return (
    <Paper
      component="section"
      aria-label="Player visibility"
      variant="outlined"
      sx={{ mt: 3, overflow: "hidden" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          borderBottom: 1,
          borderColor: "divider",
          justifyContent: "space-between",
          px: 2,
          py: 1.75,
        }}
      >
        <Box>
          <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
            Player visibility
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Facility approval and each service&apos;s setup are checked
            separately.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Chip
            color={query.data.listed ? "success" : "warning"}
            label={query.data.listed ? "Listed" : "Not listed"}
            size="small"
            variant="outlined"
          />
          {query.data.listed ? (
            <Button
              component="a"
              href={playerFacilityUrl(facilityId)}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              Preview player page
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <Stack spacing={2} sx={{ p: 2 }}>
        {(query.data.listing_blockers ?? []).map((reason) => (
          <Alert
            key={reason.code}
            severity="warning"
            action={
              <Button
                color="inherit"
                component={RouterLink}
                to={RESOLVE[reason.resolution_target]}
                size="small"
              >
                {resolutionLabel(reason.resolution_target)}
              </Button>
            }
          >
            {reason.message}
          </Alert>
        ))}
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          {enabledServices.map((item) => {
            const setupBlockers = (item.blockers ?? []).filter(
              (reason) => !listingCodes.has(reason.code),
            );
            const status =
              setupBlockers.length > 0
                ? "Setup needed"
                : !query.data.listed
                  ? listingResolutionStatus(listingCodes)
                  : item.ready
                    ? "Available"
                    : "Not available";
            return (
              <Paper
                key={item.service}
                component="article"
                variant="outlined"
                sx={{ p: 1.75 }}
              >
                <Stack spacing={1.25}>
                  <Stack
                    aria-label={`${LABELS[item.service] ?? "Service"} readiness`}
                    direction="column"
                    spacing={1}
                    sx={{
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography sx={{ fontWeight: 700 }}>
                      {LABELS[item.service] ?? "Service"}
                    </Typography>
                    <Chip
                      color={
                        status === "Available"
                          ? "success"
                          : status === "Setup needed"
                            ? "warning"
                            : "default"
                      }
                      label={status}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  {setupBlockers.map((reason) => (
                    <Stack
                      key={reason.code}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {reason.message}
                      </Typography>
                      <Button
                        component={RouterLink}
                        to={RESOLVE[reason.resolution_target]}
                        size="small"
                        sx={{ flexShrink: 0 }}
                      >
                        {resolutionLabel(reason.resolution_target)}
                      </Button>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            );
          })}
        </Box>
        {disabledServices.length > 0 ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
          >
            <Typography color="text.secondary" variant="body2">
              Not enabled: {disabledServices.join(", ")}
            </Typography>
            <Button component={RouterLink} size="small" to="/settings/facility">
              Manage services
            </Button>
          </Stack>
        ) : null}
        <Typography color="text.secondary" variant="caption">
          Listed means players can discover the facility. Live schedules,
          capacity, and payment availability are checked again when they book.
        </Typography>
      </Stack>
    </Paper>
  );
}
